import type { OnDestroy } from '@angular/core';
import { computed, Injectable, signal } from '@angular/core';

interface CursorTooltip {
  xMs: number;
  deviceId: string | null;
  values: Record<string, number | null>; // syncId → y érték
}

@Injectable()
export class PlaybackStateService implements OnDestroy {
  readonly playbackHead = signal(0); // ms — a rAF lépteti lejátszás közben, seek() írja tekeréskor
  readonly seekPosition = signal(0); // ms — csak seek() írja, fetch-trigger célra
  readonly previewHead = signal<number | null>(null); // ms — drag közben írja a navigator, mouseUp-on nullázódik
  readonly isPlaying = signal(false);

  /** Az aktuálisan megjelenítendő pozíció: drag közben previewHead, egyébként playbackHead. */
  readonly displayHead = computed(() => this.previewHead() ?? this.playbackHead());
  readonly resetZoomTrigger = signal(0);
  readonly zoomLevel = signal(10);

  /** Kurzor adatok a tooltip legendhez */
  readonly cursorTooltip = signal<CursorTooltip>({
    xMs: 0,
    deviceId: null,
    values: {},
  });
  readonly cursorClientPos = signal<{ x: number; y: number } | null>(null);

  private timer: number | undefined;
  private lastTick = 0;
  private duration = 0; // ms — a felvétel hossza, PlaybackComponent állítja be

  constructor() {
    this.startLoop();
  }

  play(): void {
    this.isPlaying.set(true);
  }

  pause(): void {
    this.isPlaying.set(false);
  }

  togglePlay(): void {
    this.isPlaying.update(v => !v);
  }

  /** A felvétel hosszát állítja be — ha a lejátszófej eléri, automatikusan megáll. */
  setDuration(ms: number): void {
    this.duration = ms;
  }

  resetZoom(): void {
    this.resetZoomTrigger.update(v => v + 1);
  }

  /** Tekerés: mouseUp-ra hívandó, ez triggereli a fetch-et is a PlaybackComponent-ben. */
  seek(ms: number): void {
    this.playbackHead.set(ms);
    this.seekPosition.set(ms);
  }

  private startLoop(): void {
    this.lastTick = performance.now();

    const tick = (now: number): void => {
      const elapsed = now - this.lastTick;
      this.lastTick = now;

      if (this.isPlaying()) {
        const next = this.playbackHead() + elapsed;

        if (this.duration > 0 && next >= this.duration) {
          this.playbackHead.set(this.duration);
          this.isPlaying.set(false);
        } else {
          this.playbackHead.set(next);
        }
      }

      this.timer = requestAnimationFrame(tick);
    };

    this.timer = requestAnimationFrame(tick);
  }

  ngOnDestroy(): void {
    if (this.timer !== undefined) {
      cancelAnimationFrame(this.timer);
    }
  }
}
